import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'react-feather';
import { useNavigate, useLocation } from 'react-router-dom';
import { Link } from "react-router-dom";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredNav, setHoveredNav] = useState(null);
  const [logoUrl, setLogoUrl] = useState('/logo3.png'); // default logo
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
// 👇 Fetch logo on mount
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contact/contact`);
        const data = await response.json();

        if (data?.logo) {
          setLogoUrl(data.logo); // use logo from backend
        } else {
          setLogoUrl('/logo3.png'); // fallback
        }
      } catch (error) {
        console.error("Failed to fetch logo:", error);
        setLogoUrl('logo3.png'); // fallback on error
      }
    };

    fetchLogo();
  }, []);
  const navItems = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'About', href: '/about' },
    { name: 'News', href: '/news' },
    { name: 'Portal', href: '/portal' },
    { name: 'Document & Guideline', href: '/doc' },
    { name: 'Contact', href: '/contact' }
  ];

  // Check if current path is active
  const isActive = (href) => location.pathname === href;

  return (
    <header className={`fixed w-full z-50 transition-all duration-500 ${isScrolled ? 'bg-white shadow-md py-2' : 'bg-white py-3'}`}>
      <div className="mx-auto px-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center"
          >
            <Link to="/" className="flex items-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-3 ${isScrolled ? 'bg-gray-200' : 'bg-white'}`}>
                {/* <img src="/logo3.png" alt="Company Logo" className="h-12" /> */}
                  <img
                  src={`${process.env.REACT_APP_API_URL}/${logoUrl}`}
                  alt="Company Logo"
                  className="h-12 object-contain"
                />
              </div>
              <span className={` font-serif ${isScrolled ? 'text-indigo-600' : 'text-indigo-600'}`}>
                <p className='text-sm font-serif font-bold'>Oromia National Regional State</p>
                <p className='text-xs font-semibold text-blue-950'>Bishoftu City Administration Finance Office</p>
              </span>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4">
            {navItems.map((item) => (
              <div 
                key={item.name} 
                className="relative"
                onMouseEnter={() => setHoveredNav(item.href)}
                onMouseLeave={() => setHoveredNav(null)}
              >
                <Link
                  to={item.href}
                  className={`relative px-2 py-1 font-medium transition-colors duration-300 font-serif ${
                    isActive(item.href) 
                      ? 'text-indigo-600 font-semibold ' 
                      : isScrolled 
                        ? 'text-gray-700 hover:text-indigo-600' 
                        : 'text-gray-600 hover:text-indigo-600'
                  }`}
                >
                  {item.name}
                  
                  {/* Hover underline */}
                  <AnimatePresence>
                    {(hoveredNav === item.href && !isActive(item.href)) && (
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: '100%' }}
                        exit={{ width: 0 }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                        className="absolute left-0 bottom-0 h-0.5 bg-indigo-300"
                      />
                    )}
                  </AnimatePresence>
                </Link>

                {/* Active underline with smooth transition */}
                {isActive(item.href) && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="absolute left-0 bottom-0 h-0.5 w-full bg-indigo-600"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </div>
            ))}
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-4 py-1 rounded-lg font-medium transition-colors ${
                isScrolled 
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
                  : 'bg-white text-indigo-600 hover:bg-gray-100 border border-indigo-600'
              }`}
              onClick={() => navigate("/user/login")}
            >
              Login
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`ml-1 px-4 py-1 rounded-lg font-medium transition-colors ${
                isScrolled 
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
              onClick={() => navigate("/user/signup")}
            >
              Signup
            </motion.button>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 focus:outline-none"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? 'text-gray-700' : 'text-blue-950'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? 'text-gray-700' : 'text-blue-950'}`} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ 
              opacity: 1,
              height: 'auto',
              transition: { duration: 0.3, ease: 'easeInOut' }
            }}
            exit={{ 
              opacity: 0, 
              height: 0,
              transition: { duration: 0.2 }
            }}
            className={`md:hidden overflow-hidden ${isScrolled ? 'bg-white' : 'bg-white shadow-lg'}`}
          >
            <div className="container mx-auto px-3 py-1">
              {navItems.map((item) => (
                <div 
                  key={item.name} 
                  className="relative py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Link
                    to={item.href}
                    className={`relative inline-block px-3 py-2 font-medium ${
                      isActive(item.href)
                        ? 'text-indigo-600'
                        : isScrolled 
                          ? 'text-gray-700 hover:bg-indigo-50' 
                          : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {item.name}
                    {isActive(item.href) && (
                      <motion.div
                        layoutId="activeIndicatorMobile"
                        className="absolute left-3 bottom-1 h-0.5 bg-indigo-600"
                        initial={false}
                        style={{ width: 'calc(100% - 1.5rem)' }} // Adjust based on padding
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      />
                    )}
                  </Link>
                </div>
              ))}
              <div className="mt-2 px-3 py-2">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full py-2 rounded-lg font-medium ${
                    isScrolled
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                  onClick={() => {
                    navigate("/user/login");
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Login
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full mt-2 py-2 rounded-lg font-medium ${
                    isScrolled
                      ? 'bg-white text-indigo-600 border border-indigo-600 hover:bg-gray-50'
                      : 'bg-white text-indigo-600 border border-indigo-600 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    navigate("/user/signup");
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Signup
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;